// game/game.ts — the Run (which IS the RunCtx handed to content) and the screen
// state machine around it.
//
// Phase 4 flow:  title -> run -> death -> summary -> new run.
// Phase 5 inserts the hub between summary and run; the seam is `toHub()` and the
// `hub` screen value, which the state machine already understands.

import type {
  BossLike,
  Draw,
  EliteRider,
  Enemy,
  EnemyDef,
  GeneratorDef,
  Pickup,
  RelicDef,
  RunCtx,
  Snapshot,
  Vec,
  ZoneConfig,
  ZoneId,
  ZoneMechanic,
} from '../types'
import { TUNING } from '../data/tuning'
import { LOGICAL_H, LOGICAL_W, Renderer, WORLD_ZOOM } from '../engine/render'
import { Loop } from '../engine/loop'
import {
  Rng,
  entropySeed,
  fixedSeedFromQuery,
  hasQueryFlag,
  queryNumber,
  queryString,
  runRng,
} from '../engine/rng'
import { audio } from '../engine/audio'
import { fx } from '../engine/fx'
import { clearParticles, emit, renderParticles, updateParticles } from '../engine/particles'
import { bakeAll, drawSprite } from '../engine/sprites'
import * as Input from '../engine/input'
import {
  ENEMY_DEFS,
  EnemyEnt,
  GENERATOR_DEFS,
  HazardEnt,
  PROJECTILES,
  PickupEnt,
  Pool,
  ProjectileEnt,
  setEntityHooks,
} from './entities'
import { SpatialHash, TILE, World } from './world'
import { Player } from './player'
import { getWeapon, weaponIds } from './weapons'
import { configureEnemy, renderEnemy, updateEnemy } from './enemies'
import { GeneratorInst, killGenerator, renderGenerator, updateGenerator } from './generators'
import { RoomState, TilePos, buildRoom, renderRoomOverlay, updateRoom } from './rooms'
import { GLOOMSPAWN_ID, Gloom } from './gloom'
import { ZoneRunner, getZone, startBossRoom } from './zones'
import { barks } from './barks'
import { freshModifiers, meta, type RunModifiers, type RunSummary } from './meta'
import {
  absorbLethal,
  drawRelicOffers,
  fireHook,
  grantRelic,
  onZoneStartRelics,
  relicEliteEmberMul,
  relicEmberMul,
  relicFoodHealMul,
  relicGoldDamage,
  relicGoldMagnetRadius,
  relicPierceAdd,
  relicPotionRadiusMul,
  relicSpecialCooldownMul,
  relicSpeedMul,
  relicDamageMul,
  resetRelics,
  setRelicPool,
} from './relics'
import { RELIC_COST_INDEX, RELIC_RARITY_COLOR } from '../data/relics'
import { runStats, type EmberBucket } from './runstats'
import { closeOverlay, overlayOpen, showOverlay, updateOverlay, type OverlayChoice } from '../ui/overlay'
import { hidePause, pauseVisible, showPause } from '../ui/pause'
import { renderHud } from './hud'

const scratch: Vec = { x: 0, y: 0 }
const scratch2: Vec = { x: 0, y: 0 }
/** Reused hook payload — relic hooks fire every frame; nothing here allocates. */
const hookEvent: { x: number; y: number } = { x: 0, y: 0 }

export type Screen = 'title' | 'run' | 'summary' | 'hub'

// ---------------------------------------------------------------------------
// The zone-1 no-op mechanic stands in until a zone registers its own.
const NULL_MECHANIC: ZoneMechanic = {
  zoneId: 1,
  onZoneStart() {},
  onRoomStart() {},
  update() {},
  onZoneEnd() {},
}

export class Run implements RunCtx {
  readonly world = new World()
  readonly hash = new SpatialHash()
  readonly player = new Player()
  readonly room = new RoomState()
  readonly gloomSys = new Gloom()
  readonly zoneRunner = new ZoneRunner()

  readonly enemyPool = new Pool<EnemyEnt>(() => new EnemyEnt())
  readonly projectilePool = new Pool<ProjectileEnt>(() => new ProjectileEnt())
  readonly pickupPool = new Pool<PickupEnt>(() => new PickupEnt())
  readonly hazardPool = new Pool<HazardEnt>(() => new HazardEnt())
  readonly generators: GeneratorInst[] = []
  readonly doors: TilePos[] = []
  readonly gates: TilePos[] = []
  gatesOpen = false

  zone: ZoneConfig
  mechanic: ZoneMechanic = NULL_MECHANIC
  zoneId: ZoneId = 1

  time = 0
  zoneTime = 0
  roomTime = 0
  embers = 0
  kills = 0
  isBossRoom = false
  /** The zone's boss while its arena is live (game/zones.ts startBossRoom). */
  boss: BossLike | null = null
  /**
   * What Snapshot.boss reports after the victory celebration removes the corpse:
   * the harness bots poll `boss.hp <= 0` on a real-time interval, and at high
   * timescale the corpse alone is on screen too briefly to be a reliable read.
   */
  bossEpitaph: { hp: number; maxHp: number; phase: number } | null = null
  /** Boss-victory celebration (bossVictory): seconds left, and the burst cursor. */
  private victoryT = 0
  private victoryBurst = 0
  private readonly victoryPos: Vec = { x: 0, y: 0 }
  finished = false
  finishReason: 'death' | 'zone-complete' | 'abandon' | null = null

  /**
   * Run-scoped switches the meta layer sets before/while a run is live.
   * Written in start() from meta.runModifiers(); the run and the zones read.
   *   curatorActive   — altars offer 3 relics instead of 2 (shrine: Curator)
   *   tollbreaker     — zone 5: the first toll door of the zone is free
   *   tollDiscountMul — zone 5: multiplier on every toll/coffer price
   *   lampRadiusMul   — zone 4: multiplier on the Warden's lamp radius
   *   pullImmune      — zones 2/6: pulls and knockback do not move the Warden
   *   graveCandle     — zone 3: the first husk in each room seals itself
   */
  readonly flags: Record<string, number | boolean> = {}

  /** The banked layer this run is standing on (DESIGN §7). */
  mods: RunModifiers = freshModifiers()
  /** Death's Refusal charges left this run, and what it revives you to. */
  reviveCharges = 0
  reviveFraction = 0.5
  /** Warding Knot: the next hit in this room is absorbed. */
  wardingReady = false
  private regenAcc = 0

  /**
   * Relic flags for *later zones* to read. Written by game/relics.ts on grant,
   * documented there. Zone mechanics read numbers, never relic ids.
   */
  readonly relicFlags: Record<string, number> = {}

  /** The itemised ledger this run keeps about itself (DESIGN §8). */
  readonly stats = runStats

  /** DESIGN §3: the respite fountain is once per RUN, not per respite. */
  fountainUses: number = TUNING.run.respiteFountainUses

  private readonly offerScratch: RelicDef[] = []
  private prevDashT = 0
  private dashTrailAcc = 0
  private dashTrailX = 0
  private dashTrailY = 0

  readonly rngObj: Rng
  readonly renderer: Renderer
  private readonly game: Game

  constructor(game: Game, renderer: Renderer, rng: Rng, zone: ZoneConfig) {
    this.game = game
    this.renderer = renderer
    this.rngObj = rng
    this.zone = zone
  }

  // ------------------------------------------------------------- RunCtx bits

  get enemies(): readonly Enemy[] {
    return this.enemyPool.live
  }

  get gloom(): { value: number; rateMul: number; add(frac: number): void } {
    return this.gloomSys
  }

  rng = (): number => this.rngObj.next()

  isWall(tileX: number, tileY: number): boolean {
    return this.world.isWall(tileX, tileY)
  }

  tileAt(p: Vec): string {
    return this.world.glyphAtPoint(p)
  }

  /** World-space centres of every tile carrying `glyph` in the current room. */
  findGlyphTiles(glyph: string): Vec[] {
    const out: Vec[] = []
    const w = this.world
    for (let ty = 0; ty < w.h; ty++) {
      for (let tx = 0; tx < w.w; tx++) {
        if (w.glyphAt(tx, ty) === glyph) out.push({ x: tx * TILE + TILE / 2, y: ty * TILE + TILE / 2 })
      }
    }
    return out
  }

  /** Impulse on the Warden (floods, pulls). Respects the pullImmune flag. */
  pushPlayer(fx2: number, fy2: number): void {
    if (this.flags.pullImmune) return
    this.player.kbx += fx2
    this.player.kby += fy2
  }

  /** Deft Hand (shrine) and Quickened Sigil (relic) stack multiplicatively. */
  specialCooldownMul(): number {
    return relicSpecialCooldownMul() * this.mods.specialCooldownMul
  }

  get specialCooldownMax(): number {
    return getWeapon(this.player.weaponId).specialCooldown * this.specialCooldownMul()
  }

  /** Real edge on the interact action, for respite fixtures. */
  interactPressed(): boolean {
    return Input.wasPressed('interact')
  }

  // ------------------------------------------------------------- lifecycle

  start(zoneId: ZoneId): boolean {
    const entry = getZone(zoneId)
    if (!entry) return false
    this.zoneId = zoneId
    // Vault Sense buys seconds on the vault clock. The zone config is shared
    // data, so the run gets a copy rather than a mutation.
    const mods = meta.runModifiers()
    this.mods = mods
    this.zone =
      mods.vaultClockAdd > 0
        ? { ...entry.config, vaultClockSeconds: entry.config.vaultClockSeconds + mods.vaultClockAdd }
        : entry.config
    this.mechanic = entry.mechanic
    if (!this.zoneRunner.start(zoneId, this.rngObj)) return false

    bakeAll(this.zone.palette)

    this.time = 0
    this.zoneTime = 0
    this.roomTime = 0
    this.embers = 0
    this.kills = 0
    this.isBossRoom = false
    this.boss = null
    this.bossEpitaph = null
    this.victoryT = 0
    this.finished = false
    this.finishReason = null
    this.gloomSys.reset(this.zone)
    // Gloompact is the base rate; relic gloom modifiers fold in on top of it
    // (recomputeRelicStats is delta-based, so this survives).
    this.gloomSys.rateMul *= mods.gloomRateMul
    resetRelics()
    setRelicPool(mods.relicPool)
    runStats.reset()
    this.fountainUses = TUNING.run.respiteFountainUses
    this.altarOffers.length = 0
    for (const k in this.relicFlags) delete this.relicFlags[k]
    this.prevDashT = 0
    barks.clear()
    clearParticles()
    fx.reset()
    closeOverlay()

    this.player.reset(0, 0, meta.data.loadout.weapon || 'cinderblade')
    this.player.applyMeta(mods)
    this.reviveCharges = Math.floor(mods.reviveCharges)
    this.wardingReady = mods.wardingAbsorb
    this.regenAcc = 0
    this.flags.curatorActive = mods.curatorActive
    this.flags.tollbreaker = mods.tollbreaker
    this.flags.tollDiscountMul = mods.tollDiscountMul
    this.flags.lampRadiusMul = mods.lampRadiusMul
    this.flags.pullImmune = mods.pullImmune
    this.flags.graveCandle = mods.graveCandle
    onZoneStartRelics(this)
    if (mods.startGold > 0) this.addGold(mods.startGold)
    runStats.beginZone(zoneId, this.zone.name)
    setEntityHooks({ onEnemyDeath: (e, silent) => this.handleEnemyDeath(e, silent) })

    meta.beginRun()
    meta.reachedZone(zoneId)
    audio.setMusic(this.zone.music)
    audio.setCombat(true)

    this.mechanic.onZoneStart(this)
    const step = this.zoneRunner.next()
    if (step.kind === 'room') {
      buildRoom(this, step.template, step.index)
    } else if (step.kind === 'respite') {
      // A zone may open on its respite (`?bossonly=1`, and any future zone whose
      // shape starts safe). Anything else means the zone has no first room.
      buildRoom(this, step.template, 0, true)
      audio.setCombat(false)
    } else {
      return false
    }
    this.bark(this.zone.tagline)
    // The one onboarding line the run allows itself: where the answers live.
    if (zoneId === 1) this.bark('Esc — pause, controls, and what your relics do.')
    if (mods.freeStartRelic) this.offerWelcomeRelic()
    return true
  }

  /**
   * The stair down: carry the SAME run (player, gold, relics, embers, kills,
   * fountain state, relic flags) into the next zone. Zone-scoped state — runner,
   * clock, gloom, palette, music, mechanic — is re-rolled. bossPayout already
   * closed the zone in runStats; this only opens the next one.
   */
  descendToZone(zoneId: ZoneId): boolean {
    const entry = getZone(zoneId)
    if (!entry) return false
    this.mechanic.onZoneEnd(this)
    this.zoneId = zoneId
    const mods = this.mods
    this.zone =
      mods.vaultClockAdd > 0
        ? { ...entry.config, vaultClockSeconds: entry.config.vaultClockSeconds + mods.vaultClockAdd }
        : entry.config
    this.mechanic = entry.mechanic
    if (!this.zoneRunner.start(zoneId, this.rngObj)) return false

    bakeAll(this.zone.palette)
    this.zoneTime = 0
    this.roomTime = 0
    this.isBossRoom = false
    this.boss = null
    this.bossEpitaph = null
    this.victoryT = 0
    this.gloomSys.reset(this.zone)
    this.gloomSys.rateMul *= mods.gloomRateMul
    this.altarOffers.length = 0
    barks.clear()
    clearParticles()
    onZoneStartRelics(this)   // re-arms once-per-zone relics (Last Ember)
    this.wardingReady = mods.wardingAbsorb
    runStats.beginZone(zoneId, this.zone.name)
    meta.reachedZone(zoneId)
    audio.setMusic(this.zone.music)
    audio.setCombat(true)

    this.mechanic.onZoneStart(this)
    const step = this.zoneRunner.next()
    if (step.kind === 'room') {
      buildRoom(this, step.template, step.index)
    } else if (step.kind === 'respite') {
      buildRoom(this, step.template, 0, true)
      audio.setCombat(false)
    } else {
      return false
    }
    this.bark(this.zone.tagline)
    return true
  }

  /** Sovereign's Welcome (shrine row 6): a free relic before the first step. */
  private offerWelcomeRelic(): void {
    drawRelicOffers(this.rngObj, TUNING.run.bossRelicChoices, this.zoneId, this.offerScratch)
    if (this.offerScratch.length === 0) return
    const choices: OverlayChoice[] = this.offerScratch.map((d) => ({
      id: d.id,
      name: d.name,
      flavor: d.flavor,
      desc: d.desc,
      tag: d.rarity.toUpperCase(),
      color: RELIC_RARITY_COLOR[d.rarity],
    }))
    this.sfx('ui')
    showOverlay({
      domId: 'welcome-relic',
      title: "THE SOVEREIGN'S WELCOME",
      sub: 'The Threshold sends you down with something. Take one.',
      choices,
      footer: '1/2/3 to take.',
      closeLabel: null,
      onChoose: (id) => {
        const def = this.offerScratch.find((d) => d.id === id)
        if (def) grantRelic(this, def)
        return 'close'
      },
    })
  }

  clearAllEntities(): void {
    this.enemyPool.clear()
    this.projectilePool.clear()
    this.pickupPool.clear()
    this.hazardPool.clear()
    this.generators.length = 0
    clearParticles()
  }

  advanceRoom(doorIndex = 0): void {
    // The stair out of a beaten boss arena. The payout already happened at the
    // kill (bossPayout), so this step leaves the zone — into the next one when
    // a deeper zone is registered, out of the run after zone 6.
    if (this.isBossRoom) {
      const next = this.zoneId + 1
      if (next <= 6 && getZone(next) && this.descendToZone(next as ZoneId)) return
      this.endRun('zone-complete')
      return
    }
    // Spurs topology (D-021): hub doors open spurs; a cleared spur's exit seals
    // it and returns to the hub. The boss stair is gated on all seals.
    if (this.zoneRunner.mode === 'spurs') {
      this.wardingReady = this.mods.wardingAbsorb
      if (this.room.isRespite) {
        const step = this.zoneRunner.spurStep(doorIndex)
        if (step && step.kind === 'room') {
          this.roomTime = 0
          buildRoom(this, step.template, step.index)
          audio.setCombat(true)
          this.bark(`The spur swallows you. Seals: ${this.zoneRunner.seals}/${this.zoneRunner.spurs.length}.`)
        }
        return
      }
      const hub = this.zoneRunner.sealSpur()
      if (hub) {
        this.roomTime = 0
        buildRoom(this, hub, 0, true)
        audio.setCombat(false)
        const s = this.zoneRunner.seals
        const n = this.zoneRunner.spurs.length
        this.addEmbers(TUNING.economy.vaultEmbers, 'other')
        this.sfx('door')
        this.bark(s >= n ? 'Three seals. The heart-gate stands open.' : `A Heartseal taken. ${s}/${n}.`)
      }
      return
    }
    const step = this.zoneRunner.next()
    // Warding Knot absorbs the first hit taken in EACH room (DESIGN §7.2).
    this.wardingReady = this.mods.wardingAbsorb
    if (step.kind === 'room') {
      this.roomTime = 0
      buildRoom(this, step.template, step.index)
      return
    }
    if (step.kind === 'respite') {
      this.roomTime = 0
      this.altarOffers.length = 0
      buildRoom(this, step.template, this.zoneRunner.order.length, true)
      audio.setCombat(false)
      this.bark('A cold chapel above the stair. Spend, drink, and go down.')
      return
    }
    if (step.kind === 'boss') {
      if (startBossRoom(this, step.bossId)) {
        this.roomTime = 0
        this.isBossRoom = true
        return
      }
      this.zoneComplete()
      return
    }
    this.zoneComplete()
  }

  // ------------------------------------------------------ respite fixtures

  /**
   * The Altar (DESIGN §3): pick 1 of 2 relics for gold — 3 with the Curator
   * shrine node, which the meta layer signals through run.flags.curatorActive —
   * plus food and a potion. One relic per respite.
   */
  openAltar(): void {
    if (overlayOpen()) return
    const room = this.room
    const n = this.flags.curatorActive ? TUNING.run.altarRelicChoices + 1 : TUNING.run.altarRelicChoices
    if (this.altarOffers.length === 0 && !room.altarRelicBought) {
      drawRelicOffers(this.rngObj, n, this.zoneId, this.offerScratch)
      for (const d of this.offerScratch) this.altarOffers.push(d)
    }
    this.sfx('ui')
    showOverlay({
      domId: 'altar',
      title: 'THE ALTAR',
      sub: 'Gold buys nothing past the stair. Spend it here.',
      gold: this.player.gold,
      choices: this.altarChoices(),
      extras: this.altarGoods(),
      footer: 'One relic per altar. 1/2/3 to buy · E to leave.',
      closeLabel: 'LEAVE  (E)',
      onChoose: (id) => this.altarBuy(id),
    })
  }

  private readonly altarOffers: RelicDef[] = []

  /** Silver Tongue (shrine row 5) discounts every altar/coffer price. */
  price(base: number): number {
    return Math.max(1, Math.round(base * this.mods.altarDiscountMul))
  }

  /** Iron Ration adds flat healing to every ration eaten. */
  foodHeal(base: number): number {
    return base * relicFoodHealMul() + this.mods.foodHealAdd
  }

  private altarChoices(): OverlayChoice[] {
    const out: OverlayChoice[] = []
    for (const d of this.altarOffers) {
      const cost = this.price(TUNING.economy.altarRelicCost[RELIC_COST_INDEX[d.rarity]])
      out.push({
        id: 'relic:' + d.id,
        name: d.name,
        flavor: d.flavor,
        desc: d.desc,
        tag: d.rarity.toUpperCase(),
        color: RELIC_RARITY_COLOR[d.rarity],
        cost,
        disabled: this.room.altarRelicBought || this.player.gold < cost,
        note: this.room.altarRelicBought ? 'altar spent' : undefined,
      })
    }
    return out
  }

  private altarGoods(): OverlayChoice[] {
    const p = this.player
    const foodCost = this.price(TUNING.economy.altarFoodCost)
    const potionCost = this.price(TUNING.economy.altarPotionCost)
    return [
      {
        id: 'good:food',
        name: 'Ration',
        flavor: 'Salt, fat and stubbornness.',
        desc: `Heals ${Math.round(this.foodHeal(TUNING.player.foodHeal))} HP on the spot.`,
        tag: 'FOOD',
        color: '#8ee87a',
        cost: foodCost,
        disabled: p.gold < foodCost || p.hp >= p.maxHp,
        note: p.hp >= p.maxHp ? 'already whole' : undefined,
      },
      {
        id: 'good:potion',
        name: 'Emberburst',
        flavor: 'One held flask. The room stops arguing.',
        desc: `+1 potion. Quaff with Q: ${TUNING.player.potionDamage} damage all around you, destroys enemy shots.`,
        tag: 'POTION',
        color: '#ff9a5c',
        cost: potionCost,
        disabled: p.gold < potionCost || p.potions >= p.potionCap,
        note: p.potions >= p.potionCap ? 'belt full' : undefined,
      },
    ]
  }

  private altarBuy(id: string): 'close' | 'stay' {
    const p = this.player
    if (id.startsWith('relic:')) {
      if (this.room.altarRelicBought) return 'stay'
      const def = this.altarOffers.find((d) => d.id === id.slice(6))
      if (!def) return 'stay'
      const cost = this.price(TUNING.economy.altarRelicCost[RELIC_COST_INDEX[def.rarity]])
      if (p.gold < cost) return 'stay'
      p.gold -= cost
      runStats.goldSpent += cost
      this.room.altarRelicBought = true
      grantRelic(this, def)
      this.featTick('relicsBought')
      updateOverlay({ gold: p.gold, choices: this.altarChoices(), extras: this.altarGoods() })
      return 'stay'
    }
    if (id === 'good:food') {
      const cost = this.price(TUNING.economy.altarFoodCost)
      if (p.gold < cost || p.hp >= p.maxHp) return 'stay'
      p.gold -= cost
      runStats.goldSpent += cost
      p.heal(this.foodHeal(TUNING.player.foodHeal))
      fireHook('onFoodEaten', this)
      this.sfx('pickup')
      updateOverlay({ gold: p.gold, choices: this.altarChoices(), extras: this.altarGoods() })
      return 'stay'
    }
    if (id === 'good:potion') {
      const cost = this.price(TUNING.economy.altarPotionCost)
      if (p.gold < cost || p.potions >= p.potionCap) return 'stay'
      p.gold -= cost
      runStats.goldSpent += cost
      p.potions++
      this.sfx('pickup')
      updateOverlay({ gold: p.gold, choices: this.altarChoices(), extras: this.altarGoods() })
      return 'stay'
    }
    return 'stay'
  }

  /** The Fountain: +HP, once per run (DESIGN §3). */
  drinkFountain(): void {
    if (this.fountainUses <= 0) {
      this.bark('Dry. You already drank.')
      this.sfx('ui')
      return
    }
    this.fountainUses--
    this.healPlayer(TUNING.player.fountainHeal)
    this.sfx('potion')
    this.shake(2, 0.25)
    emit(this.player.pos.x, this.player.pos.y, {
      count: 22,
      color: '#8fe6ff',
      speed: 140,
      life: 0.6,
      lifeVar: 0.3,
      size: 3,
      shape: 1,
    })
    this.bark('Cold water, and a little of you comes back.')
  }

  /** The stair down. Boss work replaces the fall-through with the fight. */
  enterBossDoor(): void {
    if (overlayOpen() || this.finished) return
    // Spurs (D-021): the heart-gate wants every seal before it opens.
    if (this.zoneRunner.mode === 'spurs' && this.zoneRunner.seals < this.zoneRunner.spurs.length) {
      if (this.roomTime > 1 && Math.floor(this.roomTime * 2) % 6 === 0) {
        this.bark(`The gate wants ${this.zoneRunner.spurs.length} seals. You carry ${this.zoneRunner.seals}.`)
      }
      return
    }
    const step = this.zoneRunner.next()
    if (step.kind === 'boss' && startBossRoom(this, step.bossId)) {
      this.roomTime = 0
      this.isBossRoom = true
      return
    }
    this.zoneComplete()
  }

  /**
   * Zone cleared with no boss in the way (`?noboss=1`, and any zone whose boss
   * is not built yet): pay, then end the run.
   */
  private zoneComplete(): void {
    this.bossPayout(() => this.endRun('zone-complete'))
  }

  /**
   * The zone-clear payout (DESIGN §3: "boss kill -> relic choice 1 of 3, free +
   * ember/gold payout + descend stairs"). `onDone` runs once the relic card has
   * been taken — the boss path cuts the stair there, the bossless path ends the
   * run there.
   */
  bossPayout(onDone: () => void): void {
    if (this.finished) return
    const i = Math.max(0, Math.min(TUNING.economy.bossEmbers.length - 1, this.zoneId - 1))
    this.addEmbers(TUNING.economy.bossEmbers[i] + this.mods.emberFontPerBoss, 'boss')
    this.addGold(TUNING.economy.bossGold[i])
    // Second Wind (shrine row 2): a boss down is 20 HP back.
    if (this.mods.secondWindHeal > 0) {
      this.healPlayer(this.mods.secondWindHeal)
      this.bark('Second wind. The stair can wait a moment.')
    }
    this.featTick('zonesCleared')
    runStats.endZone(this.zoneTime, true)
    this.bark(`${this.zone.name} is behind you.`)

    drawRelicOffers(this.rngObj, TUNING.run.bossRelicChoices, this.zoneId, this.offerScratch)
    if (this.offerScratch.length === 0) {
      onDone()
      return
    }
    const choices: OverlayChoice[] = this.offerScratch.map((d) => ({
      id: d.id,
      name: d.name,
      flavor: d.flavor,
      desc: d.desc,
      tag: d.rarity.toUpperCase(),
      color: RELIC_RARITY_COLOR[d.rarity],
    }))
    this.sfx('ui')
    showOverlay({
      domId: 'relic-pick',
      title: `${this.zone.name.toUpperCase()} — CLEARED`,
      sub: `+${TUNING.economy.bossEmbers[i]} embers · +${TUNING.economy.bossGold[i]} gold. Take one.`,
      choices,
      footer: this.isBossRoom ? 'The stair opens below. 1/2/3 to take.' : 'The stair below is not yet cut. 1/2/3 to take.',
      closeLabel: null,
      onChoose: (id) => {
        const def = this.offerScratch.find((d) => d.id === id)
        if (def) grantRelic(this, def)
        return 'close'
      },
      onClose: onDone,
    })
  }

  /**
   * The kill celebration (zones.ts bossDefeated lands here). Without it the
   * payout card drops onto a frozen corpse still holding its last attack on
   * screen: fanfare, staged gold bursts, then the boss and everything hostile
   * it left in the air are removed before bossPayout runs. The corpse stays
   * visible through the window — its own death effects play over it — and
   * leaves in the final flash.
   */
  bossVictory(): void {
    if (this.finished || !this.boss || this.bossEpitaph) return
    const b = this.boss
    this.bossEpitaph = { hp: 0, maxHp: b.maxHp, phase: b.phase }
    this.victoryPos.x = b.pos.x
    this.victoryPos.y = b.pos.y
    this.victoryT = TUNING.bosses.victory.seconds
    this.victoryBurst = 0
    this.clearHostileFire()
    this.sfx('victory')
    fx.vignette(1.2, '#7a5c14')
    // below the corpse, not above: a north-wall boss (the Broodgate) would put
    // the word under the HUD bars
    fx.floatText('FELLED', b.pos.x, b.pos.y + 58, '#ffd98a', 26)
  }

  /** Ticked from update() while the celebration window is open. */
  private updateVictory(dt: number): void {
    const total = TUNING.bosses.victory.seconds
    const bursts = TUNING.bosses.victory.bursts
    this.victoryT -= dt
    const gone = total - this.victoryT
    // staggered gold bursts ringing the corpse, drifting up like sparks
    while (this.victoryBurst < bursts && gone >= ((this.victoryBurst + 1) * total) / (bursts + 1)) {
      const i = this.victoryBurst++
      const a = this.rng() * Math.PI * 2
      const r = 20 + this.rng() * 55
      emit(this.victoryPos.x + Math.cos(a) * r, this.victoryPos.y + Math.sin(a) * r, {
        count: 24,
        color: i % 2 === 0 ? '#ffd98a' : '#fff3c4',
        speed: 220,
        speedVar: 150,
        life: 0.8,
        lifeVar: 0.4,
        size: 4,
        grav: -50,
        drag: 0.9,
        shape: 1,
      })
      this.shake(2.5, 0.18)
      if (i % 2 === 0) this.sfx('pickup')
    }
    if (this.victoryT > 0) return
    this.victoryT = 0
    // the corpse leaves in one flash, and the payout lands on a clear stage
    emit(this.victoryPos.x, this.victoryPos.y, {
      count: 50,
      color: '#fff8e8',
      speed: 340,
      speedVar: 220,
      life: 0.6,
      lifeVar: 0.3,
      size: 5,
      drag: 0.86,
    })
    this.shake(5, 0.35)
    this.boss = null
    this.clearHostileFire()
    this.bossPayout(() => this.openDescent())
  }

  /**
   * Everything hostile still in flight or on the floor, gone. A beaten arena
   * must hold nothing that can hurt — dead shots skip the sweep's friendlySplash
   * (it guards on friendly), so flagging them dead is the whole job.
   */
  private clearHostileFire(): void {
    const prs = this.projectilePool.live
    for (let i = 0; i < prs.length; i++) if (!prs[i].friendly) prs[i].alive = false
    const hz = this.hazardPool.live
    for (let i = 0; i < hz.length; i++) if (!hz[i].friendly) hz[i].alive = false
  }

  /**
   * Cut the stair out of a beaten boss arena. The arena's 'D' tile was sealed on
   * entry; the room is only "cleared" once the boss is down, so rooms.ts starts
   * watching the door tile here and nowhere earlier.
   */
  openDescent(): void {
    if (this.finished) return
    this.room.cleared = true
    this.openDoors()
    this.world.updateFlow(this.player.pos.x, this.player.pos.y, 0, true)
    this.sfx('door')
    this.shake(3, 0.4)
    this.bark('The wall opens. The stair goes down.')
  }

  endRun(reason: 'death' | 'zone-complete' | 'abandon'): void {
    if (this.finished) return
    this.finished = true
    this.finishReason = reason
    closeOverlay()
    this.mechanic.onZoneEnd(this)
    setEntityHooks(null)
    audio.setCombat(false)
    audio.stopMusic()
    if (reason !== 'zone-complete') runStats.endZone(this.zoneTime, false)
    const summary: RunSummary = {
      zone: this.zoneId,
      zoneName: this.zone.name,
      roomsCleared: meta.runRoomsCleared,
      kills: this.kills,
      embers: this.embers,
      gold: this.player.gold,
      timeMs: Math.round(this.time * 1000),
      died: reason === 'death',
      won: reason === 'zone-complete',
      abandoned: reason === 'abandon',
    }
    this.game.finishRun(meta.bankRun(summary))
  }

  onPlayerDeath(source: string): void {
    audio.sfx('death')
    fx.shake(9, 0.9)
    fx.hitstop(0.22)
    fx.vignette(1.4, '#2a0000')
    emit(this.player.pos.x, this.player.pos.y, {
      count: 34,
      color: '#c33',
      speed: 190,
      speedVar: 160,
      life: 0.8,
      lifeVar: 0.4,
      size: 4,
      grav: 220,
    })
    this.bark(`The deep takes you. (${source})`)
    this.endRun('death')
  }

  // --------------------------------------------------------------- spawning

  enemyDef(id: string): EnemyDef | undefined {
    return ENEMY_DEFS.get(id)
  }

  generatorDef(id: string): GeneratorDef | undefined {
    return GENERATOR_DEFS.get(id)
  }

  private readonly deadStub = new EnemyEnt()

  spawnEnemy(
    defId: string,
    pos: Vec,
    opts?: { elite?: EliteRider | null; silent?: boolean },
  ): EnemyEnt {
    const def = ENEMY_DEFS.get(defId)
    if (!def) {
      console.warn('[gauntlite] unknown enemy def:', defId)
      this.deadStub.alive = false
      return this.deadStub
    }
    let elite = opts?.elite ?? null
    if (elite === undefined) elite = null
    if (!opts || opts.elite === undefined) {
      const riders = this.zone.eliteRiders
      if (riders.length > 0 && def.role !== 'swarm' && this.rngObj.chance(this.zone.eliteRate)) {
        elite = this.rngObj.pick(riders)
      }
    }
    const e = this.enemyPool.obtain()
    configureEnemy(e, def, this.zone.statScale, elite, this.rngObj)
    e.pos.x = pos.x
    e.pos.y = pos.y
    if (!opts?.silent) {
      emit(pos.x, pos.y, { count: 6, color: '#7a6aa8', speed: 70, life: 0.3, size: 3, shape: 1 })
    }
    return e
  }

  spawnGloomspawn(): void {
    if (!ENEMY_DEFS.has(GLOOMSPAWN_ID)) return
    if (this.enemyPool.live.length > 240) return
    if (!this.world.findOpenNear(this.player.pos.x, this.player.pos.y, 220, 520, this.rngObj, scratch, 8)) return
    const e = this.spawnEnemy(GLOOMSPAWN_ID, scratch, { elite: null })
    if (!e.alive) return
    e.tags.gloomspawn = true
    emit(scratch.x, scratch.y, { count: 10, color: '#a06bff', speed: 90, life: 0.4, size: 3, shape: 1 })
  }

  spawnProjectile(defId: string, pos: Vec, dir: Vec, friendly = false): void {
    this.spawnProjectileEx(defId, pos.x, pos.y, dir.x, dir.y, friendly)
  }

  spawnProjectileAt(
    defId: string,
    x: number,
    y: number,
    dx: number,
    dy: number,
    friendly: boolean,
    targetX?: number,
    targetY?: number,
  ): ProjectileEnt | null {
    return this.spawnProjectileEx(defId, x, y, dx, dy, friendly, targetX, targetY)
  }

  spawnProjectileEx(
    defId: string,
    x: number,
    y: number,
    dx: number,
    dy: number,
    friendly: boolean,
    targetX?: number,
    targetY?: number,
  ): ProjectileEnt | null {
    const def = PROJECTILES.get(defId)
    if (!def) return null
    const l = Math.hypot(dx, dy) || 1
    const p = this.projectilePool.obtain()
    p.def = def
    p.alive = true
    p.friendly = friendly
    p.pos.x = x
    p.pos.y = y
    p.vel.x = (dx / l) * def.speed
    p.vel.y = (dy / l) * def.speed
    p.life = def.lifetime
    p.damage = def.damage * (friendly ? 1 : this.zone.statScale.damage)
    p.radius = def.radius
    p.pierce = 0
    p.bounces = def.bounces ?? 0
    p.knockback = 0
    p.color = def.color
    p.spriteId = defId
    p.rot = Math.atan2(dy, dx)
    p.hitList.length = 0
    p.splash = 0
    p.splashDamage = 0
    if (def.kind === 'lobbed') {
      const tx = targetX ?? x + (dx / l) * 220
      const ty = targetY ?? y + (dy / l) * 220
      p.startX = x
      p.startY = y
      p.targetX = tx
      p.targetY = ty
      p.totalDist = Math.max(1, Math.hypot(tx - x, ty - y))
      p.travel = 0
      p.arcH = def.arcHeight ?? 40
      p.life = p.totalDist / def.speed + 0.2
      p.splash = 34
      p.splashDamage = p.damage
    }
    return p
  }

  spawnPickup(p: Pickup): void {
    const e = this.pickupPool.obtain()
    e.kind = p.kind
    e.pos.x = p.pos.x
    e.pos.y = p.pos.y
    e.value = p.value
    e.tag = p.tag
    e.alive = true
    e.bob = this.rngObj.range(0, Math.PI * 2)
    e.life = -1
    e.spriteId =
      p.kind === 'food'
        ? this.rngObj.chance(0.5)
          ? 'pickup_food'
          : 'pickup_bread'
        : p.kind === 'gold'
          ? 'pickup_gold'
          : p.kind === 'key'
            ? 'pickup_key'
            : p.kind === 'potion'
              ? 'pickup_potion'
              : p.kind === 'custom' && p.tag === 'ember'
                ? '' // drawn procedurally; embers are light, not an object
                : 'pickup_relic'
  }

  /** Allocation-free spawnPickup for the hot paths (loot rolls, vault stocking). */
  spawnPickupAt(kind: Pickup['kind'], x: number, y: number, value: number, tag?: string): void {
    scratch2.x = x
    scratch2.y = y
    this.spawnPickup({ kind, pos: scratch2, value, tag })
  }

  removePickupsAt(pos: Vec, radius: number, kind?: Pickup['kind']): number {
    let n = 0
    const list = this.pickupPool.live
    for (let i = list.length - 1; i >= 0; i--) {
      const p = list[i]
      if (!p.alive) continue
      if (kind && p.kind !== kind) continue
      if (Math.hypot(p.pos.x - pos.x, p.pos.y - pos.y) > radius) continue
      p.alive = false
      n++
    }
    return n
  }

  spawnHazard(x: number, y: number, r: number, dps: number, seconds: number, color: string): void {
    const h = this.hazardPool.obtain()
    h.x = x
    h.y = y
    h.r = r
    h.dps = dps
    h.until = this.time + seconds
    h.born = this.time
    h.color = color
    h.friendly = true
    h.alive = true
    h.tick = 0
  }

  // --------------------------------------------------------------- damage

  /**
   * The one chokepoint every point of player-sourced damage passes through, so
   * damage relics live here instead of inside each weapon form.
   * Whetstone (×) and Gravity of Gold (+1 per 100 gold, capped) both apply.
   */
  private playerDamage(amount: number): number {
    // Edge/Warden Ascendant are the base layer; relics multiply on top.
    return amount * this.mods.damageMul * relicDamageMul() + relicGoldDamage(this.player.gold)
  }

  damageEnemy(e: EnemyEnt, rawAmount: number, srcX: number, srcY: number, knockback = 0): void {
    let amount = Math.round(this.playerDamage(rawAmount) * 10) / 10
    if (!e.alive || amount <= 0) return
    // Crumple (D-030): a staggered body takes bonus damage from every player
    // source while the stun holds. The Bulwark Maul is the stagger source — the
    // slam is the setup, whatever lands next is the payoff.
    const crumpled = typeof e.tags.stunT === 'number' && e.tags.stunT > 0
    if (crumpled) amount = Math.round(amount * TUNING.weapons.bulwarkmaul.crumpleMul * 10) / 10
    // Weapon-immune bodies (the Unlit — Phase 6 tag contract): hits ping off.
    if (e.tags.invulnerable) {
      e.hitFlashT = 0.05
      emit(e.pos.x, e.pos.y, { count: 3, color: '#c7c7d6', speed: 80, life: 0.18, size: 2, shape: 1 })
      this.sfx('ui')
      return
    }
    if (e.shielded) {
      const d = Math.hypot(this.player.pos.x - e.pos.x, this.player.pos.y - e.pos.y)
      if (d > TUNING.elites.shielded.unlockRadiusTiles * TILE) {
        e.hitFlashT = 0.05
        emit(e.pos.x, e.pos.y, { count: 3, color: '#6fc3ff', speed: 90, life: 0.2, size: 2, shape: 1 })
        return
      }
    }
    e.hp -= amount
    e.hitFlashT = 2 / 60
    if (knockback > 0 && !e.tags.noKnockback) {
      const dx = e.pos.x - srcX
      const dy = e.pos.y - srcY
      const l = Math.hypot(dx, dy) || 1
      e.kbx += (dx / l) * knockback
      e.kby += (dy / l) * knockback
    }
    fx.damageNumber(amount, e.pos.x, e.pos.y - e.radius - 8, crumpled ? '#ffb347' : undefined)
    emit(e.pos.x, e.pos.y, {
      count: 5,
      color: '#d84a3a',
      speed: 120,
      life: 0.22,
      size: 3,
      angle: Math.atan2(e.pos.y - srcY, e.pos.x - srcX),
      spread: 1.6,
    })
    if (e.hp <= 0) {
      fx.hitstop(TUNING.fx.hitstopKill)
      fx.shake(TUNING.fx.shakeHit, 0.16)
      e.kill()
    } else {
      fx.hitstop(TUNING.fx.hitstopHit)
      fx.shake(TUNING.fx.shakeHit * 0.5, 0.08)
      this.sfx('hit')
    }
  }

  /**
   * Player-sourced damage against the zone boss. The boss decides what a hit
   * means — the Broodgate's Heart pings hits off while its maws stand — so this
   * only applies the run's damage multipliers and hands it over.
   */
  damageBoss(rawAmount: number): void {
    const b = this.boss
    if (!b || !b.alive || rawAmount <= 0) return
    b.takeDamage(Math.round(this.playerDamage(rawAmount) * 10) / 10)
  }

  /** Circular hit source (splash, nova, burn trail, potion) against the boss. */
  hitBoss(x: number, y: number, radius: number, rawAmount: number): boolean {
    const b = this.boss
    if (!b || !b.alive) return false
    if (Math.hypot(b.pos.x - x, b.pos.y - y) > radius + b.radius) return false
    this.damageBoss(rawAmount)
    return true
  }

  damageGenerator(g: GeneratorInst, rawAmount: number): void {
    const amount = Math.round(this.playerDamage(rawAmount) * 10) / 10
    if (!g.alive || amount <= 0) return
    g.hp -= amount
    g.hitFlashT = 2 / 60
    fx.damageNumber(amount, g.pos.x, g.pos.y - g.radius - 8)
    emit(g.pos.x, g.pos.y, { count: 4, color: '#cfc7ad', speed: 110, life: 0.25, size: 3 })
    if (g.hp <= 0) {
      const gx = g.pos.x
      const gy = g.pos.y
      this.emberBucketHint = 'kills' // a cut source is a kill, not a rounding bin
      killGenerator(this, g)
      this.emberBucketHint = 'other'
      runStats.generatorsDown++
      // Ashfall and friends: the source answers on the way out.
      hookEvent.x = gx
      hookEvent.y = gy
      fireHook('onGeneratorKilled', this, hookEvent)
      // orphaned children keep fighting; the source is what stops
    } else {
      this.sfx('hit')
      fx.hitstop(TUNING.fx.hitstopHit)
    }
  }

  /**
   * TEST SEAM — `?invuln=1` stops the Warden taking damage. Snapshot exposes
   * enemy *counts* but not positions, so an automated policy cannot kite; without
   * this the run-flow specs (respite, vault, altar, zone reward) are undrivable.
   * It changes nothing else: rooms still have to be cleared, the vault still has
   * to be entered under its clock, the altar still has to be paid for. Never set
   * during normal play, and never set by the death/losability specs.
   */
  private readonly invuln = hasQueryFlag('invuln')

  damagePlayer(amount: number, source: string): void {
    if (this.invuln) return
    // Form passive (D-030): the Bulwark Maul's 25% damage reduction — the tank
    // identity as a stat, applied to everything that reaches the Warden.
    const dtm = getWeapon(this.player.weaponId).damageTakenMul
    if (dtm != null) amount *= dtm
    // Warding Knot (talisman): the first hit taken in each room is eaten whole.
    if (this.wardingReady && amount > 0 && this.player.alive && this.player.iframes <= 0) {
      this.wardingReady = false
      this.player.iframes = Math.max(this.player.iframes, TUNING.player.contactIframes)
      this.sfx('ui')
      fx.floatText('WARDED', this.player.pos.x, this.player.pos.y - 24, '#8fe6ff', 13)
      emit(this.player.pos.x, this.player.pos.y, {
        count: 18,
        color: '#8fe6ff',
        speed: 150,
        life: 0.45,
        size: 3,
        shape: 1,
      })
      return
    }
    // Last Ember (DESIGN §6): survive one lethal blow per zone, at 1 HP.
    this.player.hurt(this, absorbLethal(this, amount), source)
  }

  healPlayer(amount: number): void {
    this.player.heal(amount)
  }

  addGold(n: number): void {
    if (n <= 0) return
    this.player.gold += n
    runStats.goldEarned += n
  }

  /**
   * `bucket` itemises the summary (DESIGN §8). Callers that cannot name a bucket
   * — content, and game/generators.ts paying out a dead spawner — land in
   * `emberBucketHint`, which the paying site sets around the call.
   */
  private emberBucketHint: EmberBucket = 'other'

  addEmbers(n: number, bucket?: EmberBucket): void {
    if (n <= 0) return
    // Ember Sense / Ember Charm are the base layer; relic bonuses multiply.
    const total = Math.round(n * this.mods.emberMul * relicEmberMul())
    if (total <= 0) return
    this.embers += total
    runStats.ember(bucket ?? this.emberBucketHint, total)
    meta.addEmbers(total)
  }

  bark(text: string): void {
    barks.push(text)
  }

  sfx(name: string): void {
    audio.sfx(name)
  }

  shake(intensity: number, seconds: number): void {
    fx.shake(intensity, seconds)
  }

  hitstop(seconds: number): void {
    fx.hitstop(seconds)
  }

  featTick(track: string, n = 1): void {
    meta.featTick(track, n)
    runStats.feat(track, n)
  }

  openDoors(): void {
    for (const d of this.doors) this.world.setSolid(d.tx, d.ty, false)
  }

  lockDoors(): void {
    for (const d of this.doors) this.world.setSolid(d.tx, d.ty, true)
  }

  /** DESIGN §2 — your own fire destroys the food on the floor. */
  destroyFoodInArc(cx: number, cy: number, dir: number, arc: number, range: number): void {
    const list = this.pickupPool.live
    const half = arc / 2
    for (let i = list.length - 1; i >= 0; i--) {
      const p = list[i]
      if (!p.alive || p.kind !== 'food') continue
      const dx = p.pos.x - cx
      const dy = p.pos.y - cy
      const d = Math.hypot(dx, dy)
      if (d > range) continue
      if (d > 4) {
        let a = Math.atan2(dy, dx) - dir
        while (a > Math.PI) a -= Math.PI * 2
        while (a < -Math.PI) a += Math.PI * 2
        if (Math.abs(a) > half) continue
      }
      this.destroyFood(p)
    }
  }

  private destroyFood(p: PickupEnt): void {
    p.alive = false
    emit(p.pos.x, p.pos.y, { count: 8, color: '#a8503c', speed: 110, life: 0.3, size: 3, grav: 240 })
    fx.floatText('ruined', p.pos.x, p.pos.y - 10, '#a8503c', 10)
  }

  /** Emberburst: the potion clears the room's pressure, not the room. */
  emberburst(x: number, y: number, baseRadius: number, damage: number, isPotion = true): void {
    const radius = isPotion ? baseRadius * relicPotionRadiusMul() : baseRadius
    const list = this.enemyPool.live
    for (let i = list.length - 1; i >= 0; i--) {
      const e = list[i]
      if (!e.alive) continue
      const d = Math.hypot(e.pos.x - x, e.pos.y - y)
      if (d > radius) continue
      const wasShielded = e.shielded
      e.shielded = false
      this.damageEnemy(e, damage, x, y, 260)
      if (e.alive) {
        e.shielded = wasShielded
        e.state = 3
        e.stateT = 0
        e.cool = Math.max(e.cool, 0.9)
      }
    }
    for (let i = 0; i < this.generators.length; i++) {
      const g = this.generators[i]
      if (!g.alive) continue
      if (Math.hypot(g.pos.x - x, g.pos.y - y) <= radius) this.damageGenerator(g, damage)
    }
    this.hitBoss(x, y, radius, damage)
    const pr = this.projectilePool.live
    for (let i = pr.length - 1; i >= 0; i--) {
      const p = pr[i]
      if (!p.alive || p.friendly) continue
      if (Math.hypot(p.pos.x - x, p.pos.y - y) <= radius) p.alive = false
    }
    emit(x, y, { count: 48, color: '#ffb347', speed: 340, speedVar: 240, life: 0.55, lifeVar: 0.25, size: 5, drag: 0.87 })
    emit(x, y, { count: 24, color: '#fff3c4', speed: 180, life: 0.35, size: 4, shape: 1 })
  }

  // ------------------------------------------------------------ enemy death

  private handleEnemyDeath(e: EnemyEnt, silent: boolean): void {
    if (e.genIndex >= 0 && e.genIndex < this.generators.length) {
      const g = this.generators[e.genIndex]
      if (g.live > 0) g.live--
    }
    if (silent) return

    this.kills++
    meta.addKill(e.def.id)
    runStats.kill(e.def.name)
    // A zero-ember def pays zero — the floor only protects paying enemies from
    // elite-multiplier rounding. Manufacturing value here is how the Gloom
    // became a 170-embers/minute farm (D-012, D-015).
    const emberBase = e.def.emberValue
    if (emberBase > 0) {
      this.addEmbers(Math.max(1, Math.round(emberBase * (e.elite ? relicEliteEmberMul() : 1))), 'kills')
    }
    this.sfx(e.def.sfx?.die ?? 'kill')
    emit(e.pos.x, e.pos.y, {
      count: 14,
      color: '#8a2a2a',
      speed: 170,
      speedVar: 130,
      life: 0.45,
      lifeVar: 0.2,
      size: 3,
      grav: 200,
      drag: 0.88,
    })
    emit(e.pos.x, e.pos.y, { count: 6, color: '#e0d6c0', speed: 90, life: 0.5, size: 3, shape: 1 })

    if (this.rngObj.chance(e.def.goldChance)) {
      scratch2.x = e.pos.x
      scratch2.y = e.pos.y
      this.spawnPickup({ kind: 'gold', pos: scratch2, value: TUNING.economy.goldSmall })
    }
    if (e.tags.carriesKey) {
      scratch2.x = e.pos.x
      scratch2.y = e.pos.y
      this.spawnPickup({ kind: 'key', pos: scratch2, value: 1 })
      this.bark('It carried a key.')
    }
    if (e.elite === 'gloomtouched') {
      for (let i = 0; i < TUNING.elites.gloomtouched.spawnCount; i++) {
        if (!this.world.findOpenNear(e.pos.x, e.pos.y, 8, 48, this.rngObj, scratch, 8)) continue
        const s = this.spawnEnemy(GLOOMSPAWN_ID, scratch, { elite: null })
        if (s.alive) s.tags.gloomspawn = true
      }
    }
    this.mechanic.onEnemyDeath?.(this, e)
    hookEvent.x = e.pos.x
    hookEvent.y = e.pos.y
    fireHook('goldOnKill', this, hookEvent)
  }

  // ---------------------------------------------------------------- update

  update(dt: number): void {
    if (this.finished) return
    this.time += dt
    this.zoneTime += dt
    this.roomTime += dt

    runStats.tickZone(this.zoneTime)

    this.world.updateFlow(this.player.pos.x, this.player.pos.y, dt)
    this.hash.build(this.enemyPool.live)

    // Quickstep and friends: PlayerState.speedMul is the zone mechanics' channel,
    // so relic speed is folded in here each step rather than written once.
    // Fleetfoot (shrine) is the base this multiplies.
    this.player.speedMul = this.mods.speedMul * relicSpeedMul()

    // Stubborn Cinder: +HP/s while nothing is trying to kill you.
    if (this.mods.regenPerSec > 0 && this.player.alive && this.player.hp < this.player.maxHp) {
      const calm = this.room.cleared || this.room.isRespite || this.enemyPool.live.length === 0
      if (calm) {
        this.regenAcc += this.mods.regenPerSec * dt
        if (this.regenAcc >= 1) {
          const whole = Math.floor(this.regenAcc)
          this.regenAcc -= whole
          this.player.hp = Math.min(this.player.maxHp, this.player.hp + whole)
        }
      } else {
        this.regenAcc = 0
      }
    }

    const projectilesBefore = this.projectilePool.live.length
    this.player.update(this, dt)
    if (this.finished) return
    this.applyProjectileRelics(projectilesBefore)
    this.updateDashRelics()

    // Quickened Sigil: the weapon sets its own cooldown, so the relic buys the
    // difference back as extra decay. Effective cooldown = advertised × mul.
    const specMul = this.specialCooldownMul()
    if (specMul < 1 && this.player.specialCd > 0) {
      this.player.specialCd -= dt * (1 / specMul - 1)
      if (this.player.specialCd < 0) this.player.specialCd = 0
    }

    for (let i = 0; i < this.generators.length; i++) updateGenerator(this, this.generators[i], dt)

    // The boss runs after its parts so it can re-arm their pour timers, and
    // before the bodies so anything it pours this step is alive this step.
    if (this.boss) this.boss.update(this, dt)
    if (this.finished) return
    // A dead boss silences its arena while it falls: the Sovereign collapses for
    // two full seconds before bossVictory runs, and anything its swarm still had
    // in flight would land on a player who has already won.
    if (this.boss && !this.boss.alive) this.clearHostileFire()
    if (this.victoryT > 0) this.updateVictory(dt)
    if (this.finished) return

    const enemies = this.enemyPool.live
    for (let i = 0; i < enemies.length; i++) updateEnemy(this, enemies[i], dt)

    this.updateContactDamage(dt)
    this.updateProjectiles(dt)
    this.updateHazards(dt)
    this.updatePickups(dt)

    // sweep the dead
    for (let i = enemies.length - 1; i >= 0; i--) if (!enemies[i].alive) this.enemyPool.releaseAt(i)
    const pr = this.projectilePool.live
    for (let i = pr.length - 1; i >= 0; i--) {
      if (pr[i].alive) continue
      this.friendlySplash(pr[i])
      this.projectilePool.releaseAt(i)
    }
    const pk = this.pickupPool.live
    for (let i = pk.length - 1; i >= 0; i--) if (!pk[i].alive) this.pickupPool.releaseAt(i)
    const hz = this.hazardPool.live
    for (let i = hz.length - 1; i >= 0; i--) if (!hz[i].alive) this.hazardPool.releaseAt(i)

    this.gloomSys.update(this, dt)
    // Zone speed layer is declarative-per-frame: reset, then the mechanic
    // re-asserts it (water slow …). The player reads it next step (1-frame lag).
    this.player.zoneSpeedMul = 1
    this.mechanic.update(this, dt)
    updateRoom(this, dt)
    updateParticles(dt)
    fx.update(dt)
    barks.update(dt)

    audio.setCombat(!this.room.cleared && this.enemyPool.live.length > 0)
  }

  /** Keen Shaft: projectiles the player just made get the extra pierce. */
  private applyProjectileRelics(from: number): void {
    const add = relicPierceAdd()
    if (add <= 0) return
    const list = this.projectilePool.live
    for (let i = from; i < list.length; i++) {
      const pr = list[i]
      if (pr.alive && pr.friendly) pr.pierce += add
    }
  }

  /** Tinder Wake: a plain dash (not the blade's lunge) leaves the floor burning. */
  private updateDashRelics(): void {
    const p = this.player
    const dashing = p.dashT > 0 && !p.lungeActive
    if (dashing && this.prevDashT <= 0) {
      this.dashTrailAcc = 0
      this.dashTrailX = p.pos.x
      this.dashTrailY = p.pos.y
      hookEvent.x = p.pos.x
      hookEvent.y = p.pos.y
      fireHook('dashBurnTrail', this, hookEvent)
    } else if (dashing) {
      this.dashTrailAcc += Math.hypot(p.pos.x - this.dashTrailX, p.pos.y - this.dashTrailY)
      this.dashTrailX = p.pos.x
      this.dashTrailY = p.pos.y
      if (this.dashTrailAcc > 24) {
        this.dashTrailAcc = 0
        hookEvent.x = p.pos.x
        hookEvent.y = p.pos.y
        fireHook('dashBurnTrail', this, hookEvent)
      }
    }
    this.prevDashT = p.dashT
  }

  private updateContactDamage(_dt: number): void {
    if (!this.player.alive || this.player.iframes > 0) return
    const p = this.player
    const weapon = getWeapon(p.weaponId)
    const list = this.enemyPool.live
    for (let i = 0; i < list.length; i++) {
      const e = list[i]
      if (!e.alive || e.spawnGrace > 0) continue
      if (e.touchDamage <= 0) continue
      const d = Math.hypot(e.pos.x - p.pos.x, e.pos.y - p.pos.y)
      if (d > e.radius + p.radius) continue
      // A form guard (Warden's Wall) can hold a body off — but only that body;
      // keep checking the rest, the shield has one face.
      if (weapon.blockContact?.(this, e)) continue
      this.damagePlayer(e.touchDamage, e.def.id)
      return
    }
  }

  private updateProjectiles(dt: number): void {
    const list = this.projectilePool.live
    const p = this.player
    for (let i = list.length - 1; i >= 0; i--) {
      const pr = list[i]
      if (!pr.alive) continue
      pr.life -= dt
      if (pr.life <= 0) {
        if (pr.def.kind === 'lobbed') this.lobbedImpact(pr)
        pr.alive = false
        continue
      }

      if (pr.def.kind === 'homing' && !pr.friendly) {
        const dx = p.pos.x - pr.pos.x
        const dy = p.pos.y - pr.pos.y
        const want = Math.atan2(dy, dx)
        let cur = Math.atan2(pr.vel.y, pr.vel.x)
        let diff = want - cur
        while (diff > Math.PI) diff -= Math.PI * 2
        while (diff < -Math.PI) diff += Math.PI * 2
        const turn = (pr.def.homingTurn ?? 2.4) * dt
        cur += Math.max(-turn, Math.min(turn, diff))
        pr.vel.x = Math.cos(cur) * pr.def.speed
        pr.vel.y = Math.sin(cur) * pr.def.speed
      }

      const nx = pr.pos.x + pr.vel.x * dt
      const ny = pr.pos.y + pr.vel.y * dt

      if (pr.def.kind === 'lobbed') {
        // arcs over geometry; only the landing matters
        pr.travel += Math.hypot(nx - pr.pos.x, ny - pr.pos.y)
        pr.pos.x = nx
        pr.pos.y = ny
        if (pr.travel >= pr.totalDist) {
          this.lobbedImpact(pr)
          pr.alive = false
        }
        continue
      }

      if (this.world.solidAtPoint(nx, ny)) {
        if (pr.bounces > 0) {
          pr.bounces--
          if (this.world.solidAtPoint(nx, pr.pos.y)) pr.vel.x = -pr.vel.x
          if (this.world.solidAtPoint(pr.pos.x, ny)) pr.vel.y = -pr.vel.y
          pr.rot = Math.atan2(pr.vel.y, pr.vel.x)
        } else {
          emit(pr.pos.x, pr.pos.y, { count: 5, color: pr.color, speed: 90, life: 0.2, size: 2, shape: 1 })
          pr.alive = false
          continue
        }
      } else {
        pr.pos.x = nx
        pr.pos.y = ny
        pr.rot = Math.atan2(pr.vel.y, pr.vel.x)
      }

      if (pr.friendly) {
        // your own fire eats the floor food
        const pk = this.pickupPool.live
        for (let j = pk.length - 1; j >= 0; j--) {
          const f = pk[j]
          if (!f.alive || f.kind !== 'food') continue
          if (Math.hypot(f.pos.x - pr.pos.x, f.pos.y - pr.pos.y) < pr.radius + 9) this.destroyFood(f)
        }
        const list2 = this.enemyPool.live
        for (let j = list2.length - 1; j >= 0; j--) {
          const e = list2[j]
          if (!e.alive) continue
          if (pr.hitList.indexOf(e) >= 0) continue
          if (Math.hypot(e.pos.x - pr.pos.x, e.pos.y - pr.pos.y) > e.radius + pr.radius) continue
          pr.hitList.push(e)
          this.damageEnemy(e, pr.damage, pr.pos.x - pr.vel.x, pr.pos.y - pr.vel.y, pr.knockback)
          if (pr.pierce > 0) {
            pr.pierce--
          } else {
            pr.alive = false
            break
          }
        }
        if (!pr.alive) continue
        for (let j = 0; j < this.generators.length; j++) {
          const g = this.generators[j]
          if (!g.alive) continue
          if (Math.hypot(g.pos.x - pr.pos.x, g.pos.y - pr.pos.y) > g.radius + pr.radius) continue
          this.damageGenerator(g, pr.damage)
          pr.alive = false
          break
        }
        // the boss body stops a shot like anything else — a sealed boss still
        // eats the projectile, it just answers with a ping instead of blood
        if (pr.alive && this.boss && this.boss.alive) {
          if (this.hitBoss(pr.pos.x, pr.pos.y, pr.radius, pr.damage)) {
            pr.alive = false
          } else {
            // still flying, still missed: a segmented boss may want to answer
            this.boss.shotGlance?.(pr.pos.x, pr.pos.y, pr.radius)
          }
        }
      } else if (p.alive) {
        if (Math.hypot(p.pos.x - pr.pos.x, p.pos.y - pr.pos.y) <= p.radius + pr.radius) {
          this.damagePlayer(pr.damage, pr.def.id)
          pr.alive = false
        }
      }
    }
  }

  /**
   * Storm Staff: a friendly straight bolt carrying splash bursts wherever it
   * stopped — enemy, generator, boss or wall. Splash is *fire*, so it ruins
   * floor food the way every other shot does (D-010); only melee arcs and the
   * shield shove are safe around a ration.
   * Called once per projectile from the dead-projectile sweep, so every way a
   * bolt can end (hit, wall, expiry) lands here exactly once.
   */
  private friendlySplash(pr: ProjectileEnt): void {
    if (!pr.friendly || pr.splash <= 0 || pr.splashDamage <= 0) return
    if (pr.def.kind === 'lobbed') return // lobbed already burst in lobbedImpact
    const x = pr.pos.x
    const y = pr.pos.y
    const r = pr.splash
    const dmg = pr.splashDamage
    const list = this.enemyPool.live
    for (let i = list.length - 1; i >= 0; i--) {
      const e = list[i]
      if (!e.alive) continue
      if (Math.hypot(e.pos.x - x, e.pos.y - y) > r + e.radius) continue
      this.damageEnemy(e, dmg, x, y, 40)
    }
    for (let i = 0; i < this.generators.length; i++) {
      const g = this.generators[i]
      if (!g.alive) continue
      if (Math.hypot(g.pos.x - x, g.pos.y - y) > r + g.radius) continue
      this.damageGenerator(g, dmg)
    }
    this.hitBoss(x, y, r, dmg)
    const pk = this.pickupPool.live
    for (let i = pk.length - 1; i >= 0; i--) {
      const f = pk[i]
      if (!f.alive || f.kind !== 'food') continue
      if (Math.hypot(f.pos.x - x, f.pos.y - y) <= r) this.destroyFood(f)
    }
    emit(x, y, {
      count: 12,
      color: pr.color,
      speed: 180,
      speedVar: 120,
      life: 0.28,
      lifeVar: 0.12,
      size: 3,
      drag: 0.86,
      shape: 1,
    })
  }

  private lobbedImpact(pr: ProjectileEnt): void {
    const x = pr.targetX
    const y = pr.targetY
    emit(x, y, { count: 16, color: pr.color, speed: 150, life: 0.4, lifeVar: 0.2, size: 4, shape: 1 })
    fx.shake(1.5, 0.12)
    if (!pr.friendly && this.player.alive) {
      if (Math.hypot(this.player.pos.x - x, this.player.pos.y - y) <= pr.splash + this.player.radius) {
        this.damagePlayer(pr.splashDamage, pr.def.id)
      }
    }
  }

  private updateHazards(dt: number): void {
    const list = this.hazardPool.live
    for (let i = list.length - 1; i >= 0; i--) {
      const h = list[i]
      if (!h.alive) continue
      if (this.time >= h.until) {
        h.alive = false
        continue
      }
      h.tick -= dt
      if (h.tick > 0) continue
      h.tick = 0.25
      const dmg = h.dps * 0.25
      const enemies = this.enemyPool.live
      for (let j = enemies.length - 1; j >= 0; j--) {
        const e = enemies[j]
        if (!e.alive) continue
        if (Math.hypot(e.pos.x - h.x, e.pos.y - h.y) > h.r + e.radius) continue
        this.damageEnemy(e, dmg, h.x, h.y, 0)
      }
      for (let j = 0; j < this.generators.length; j++) {
        const g = this.generators[j]
        if (!g.alive) continue
        if (Math.hypot(g.pos.x - h.x, g.pos.y - h.y) > h.r + g.radius) continue
        this.damageGenerator(g, dmg)
      }
      this.hitBoss(h.x, h.y, h.r, dmg)
    }
  }

  private updatePickups(dt: number): void {
    const p = this.player
    if (!p.alive) return
    // Gravity of Gold (relic) and Coin Feather (talisman): coins lean your way.
    const magnet = Math.max(relicGoldMagnetRadius(), this.mods.goldMagnetRadius)
    const list = this.pickupPool.live
    for (let i = list.length - 1; i >= 0; i--) {
      const k = list[i]
      if (!k.alive) continue
      k.bob += dt
      const dx = p.pos.x - k.pos.x
      const dy = p.pos.y - k.pos.y
      const d = Math.hypot(dx, dy)
      if (magnet > 0 && d > p.radius + 12 && d < magnet && (k.kind === 'gold' || k.tag === 'ember')) {
        const pull = (260 * (1 - d / magnet) + 90) * dt
        k.pos.x += (dx / (d || 1)) * pull
        k.pos.y += (dy / (d || 1)) * pull
      }
      if (d > p.radius + 12) continue
      if (this.mechanic.onPickupTouched && this.mechanic.onPickupTouched(this, k) === false) continue
      k.alive = false
      switch (k.kind) {
        case 'food':
          p.heal(this.foodHeal(k.value))
          this.sfx('pickup')
          fireHook('onFoodEaten', this)
          break
        case 'gold':
          this.addGold(k.value)
          this.sfx('pickup')
          fx.floatText('+' + k.value, k.pos.x, k.pos.y - 10, '#e3b23c', 11)
          break
        case 'key':
          p.keys++
          this.sfx('pickup')
          this.bark('A key. Something here is locked.')
          break
        case 'potion':
          if (p.potions < p.potionCap) {
            p.potions++
            this.sfx('pickup')
            fx.floatText('POTION', k.pos.x, k.pos.y - 12, '#ff9a5c', 11)
          } else {
            k.alive = true
          }
          break
        default:
          if (k.tag === 'ember') {
            this.addEmbers(k.value, 'vault')
            fx.floatText('+' + k.value + ' EMBERS', k.pos.x, k.pos.y - 12, '#ffb347', 11)
          }
          this.sfx('pickup')
          break
      }
    }
  }

  // ---------------------------------------------------------------- render

  render(d: Draw): void {
    const ctx = d.ctx
    const r = this.renderer

    r.beginWorld()

    // tile layer — blit only the visible slice
    if (this.world.layer) {
      const sx = Math.max(0, Math.floor(r.camera.x - fx.offX / r.zoom) - 32)
      const sy = Math.max(0, Math.floor(r.camera.y - fx.offY / r.zoom) - 32)
      const sw = Math.min(this.world.pixelW - sx, r.viewW + 96)
      const sh = Math.min(this.world.pixelH - sy, r.viewH + 96)
      if (sw > 0 && sh > 0) ctx.drawImage(this.world.layer, sx, sy, sw, sh, sx, sy, sw, sh)
    }

    this.mechanic.renderUnder?.(d)

    // hazards (burn trails) under everything alive
    const hz = this.hazardPool.live
    for (let i = 0; i < hz.length; i++) {
      const h = hz[i]
      if (!h.alive) continue
      const remain = Math.max(0, Math.min(1, (h.until - this.time) / 1.2))
      const pulse = 0.55 + 0.25 * Math.sin(this.time * 9 + h.x * 0.1)
      ctx.globalAlpha = 0.34 * remain * pulse
      ctx.fillStyle = h.color
      ctx.beginPath()
      ctx.arc(h.x, h.y, h.r, 0, Math.PI * 2)
      ctx.fill()
      ctx.globalAlpha = 0.2 * remain
      ctx.fillStyle = '#ffe08a'
      ctx.beginPath()
      ctx.arc(h.x, h.y, h.r * 0.55, 0, Math.PI * 2)
      ctx.fill()
      ctx.globalAlpha = 1
    }

    // pickups
    const pk = this.pickupPool.live
    for (let i = 0; i < pk.length; i++) {
      const k = pk[i]
      if (!k.alive) continue
      const bob = Math.sin(k.bob * 3) * 2
      if (k.tag === 'ember') {
        // Embers are light, not an object: no shadow, no sprite, just a mote.
        const pulse = 0.6 + 0.4 * Math.sin(k.bob * 5)
        ctx.globalAlpha = 0.35 * pulse
        ctx.fillStyle = '#ff9a3c'
        ctx.beginPath()
        ctx.arc(k.pos.x, k.pos.y + bob, 11 + pulse * 3, 0, Math.PI * 2)
        ctx.fill()
        ctx.globalAlpha = 1
        ctx.fillStyle = '#ffe9a8'
        ctx.beginPath()
        ctx.arc(k.pos.x, k.pos.y + bob, 4, 0, Math.PI * 2)
        ctx.fill()
        continue
      }
      ctx.globalAlpha = 0.3
      ctx.fillStyle = '#000000'
      ctx.beginPath()
      ctx.ellipse(k.pos.x, k.pos.y + 7, 7, 3, 0, 0, Math.PI * 2)
      ctx.fill()
      ctx.globalAlpha = 1
      drawSprite(ctx, k.spriteId, k.pos.x, k.pos.y + bob)
    }

    renderRoomOverlay(ctx, this)

    for (let i = 0; i < this.generators.length; i++) {
      renderGenerator(ctx, this.generators[i], this.gloomSys.generatorRateMul())
    }

    // depth sort so bodies overlap correctly (insertion sort: cheap, no alloc)
    const enemies = this.enemyPool.live
    for (let i = 1; i < enemies.length; i++) {
      const e = enemies[i]
      let j = i - 1
      while (j >= 0 && enemies[j].pos.y > e.pos.y) {
        enemies[j + 1] = enemies[j]
        j--
      }
      enemies[j + 1] = e
    }
    for (let i = 0; i < enemies.length; i++) renderEnemy(ctx, enemies[i])

    this.player.render(ctx, this.time)

    // The boss draws after the bodies: it is architecture, its parts stand in
    // the wall behind the fight, and its telegraphed lanes must never be hidden
    // by the swarm they spawned. Projectiles still land on top of it.
    if (this.boss) this.boss.render(d)

    // projectiles above bodies (readability law: smaller drawn over larger)
    const prs = this.projectilePool.live
    for (let i = 0; i < prs.length; i++) {
      const pr = prs[i]
      if (!pr.alive) continue
      let y = pr.pos.y
      if (pr.def.kind === 'lobbed') {
        const t = Math.max(0, Math.min(1, pr.travel / pr.totalDist))
        y -= Math.sin(t * Math.PI) * pr.arcH
        ctx.globalAlpha = 0.25
        ctx.fillStyle = '#000000'
        ctx.beginPath()
        ctx.ellipse(pr.pos.x, pr.pos.y, pr.radius, pr.radius * 0.5, 0, 0, Math.PI * 2)
        ctx.fill()
        ctx.globalAlpha = 1
      }
      ctx.globalAlpha = 0.35
      ctx.fillStyle = pr.color
      ctx.beginPath()
      ctx.arc(pr.pos.x, y, pr.radius + 3, 0, Math.PI * 2)
      ctx.fill()
      ctx.globalAlpha = 1
      drawSprite(ctx, pr.spriteId, pr.pos.x, y, { rot: pr.def.kind === 'straight' ? pr.rot : 0 })
    }

    renderParticles(ctx)
    fx.renderWorld(ctx)
    this.mechanic.renderOver?.(d)

    r.endWorld()
  }
}

// ===========================================================================
// The shell: screens, boot, test API.
// ===========================================================================

export class Game {
  screen: Screen = 'title'
  run: Run | null = null
  renderer!: Renderer
  loop!: Loop
  readonly errors: string[] = []
  lastSummary: RunSummary | null = null
  /** Esc pause (DESIGN §2). The sim holds; the pause menu (ui/pause.ts) is up. */
  paused = false

  private titleT = 0
  private lastRenderMs = 0
  private nextSeed: number | null = null
  private fixedSeed: number | null = null
  private onSummary: ((s: RunSummary) => void) | null = null
  private onScreenChange: ((s: Screen) => void) | null = null

  boot(canvas: HTMLCanvasElement): void {
    this.renderer = new Renderer(canvas)
    this.fixedSeed = fixedSeedFromQuery()
    meta.boot()
    fx.shakeScale = meta.data.settings.shake
    audio.setVolume(meta.data.settings.volume)

    // Bake with a neutral palette so the title screen has sprites before a zone
    // is chosen; start() re-bakes with the zone's palette.
    const first = getZone(1)
    bakeAll(
      first?.config.palette ?? {
        floor: '#3a3a44',
        floorAlt: '#32323c',
        wall: '#4a4a58',
        wallEdge: '#6a6a7a',
        accent: '#c9a227',
        glow: '#ffb347',
        bg: '#0b0c12',
      },
    )

    Input.initInput(canvas, () => audio.unlock())
    this.loop = new Loop(
      (dt) => this.step(dt),
      () => this.render(),
      (e) => this.recordError(e),
    )
    fx.attachLoop(this.loop)
    const speed = queryNumber('speed')
    if (speed && speed > 0) this.loop.timescale = Math.min(64, speed)
    // ?weapon=whisperbow — the hub weapon rack arrives in Phase 5; until then
    // this is how a form other than the starter gets played and tested.
    const weapon = queryString('weapon')
    if (weapon && weaponIds().includes(weapon)) meta.data.loadout.weapon = weapon
    this.loop.start()
  }

  onSummaryShown(cb: (s: RunSummary) => void): void {
    this.onSummary = cb
  }

  onScreen(cb: (s: Screen) => void): void {
    this.onScreenChange = cb
  }

  recordError(e: unknown): void {
    const msg = e instanceof Error ? `${e.message}\n${e.stack ?? ''}` : String(e)
    if (this.errors.length < 64) this.errors.push(msg)
  }

  private setScreen(s: Screen): void {
    if (this.screen === s) return
    this.screen = s
    this.onScreenChange?.(s)
  }

  // ------------------------------------------------------------------ flow

  startRun(): void {
    const seed = this.nextSeed ?? this.fixedSeed ?? entropySeed()
    this.nextSeed = null
    runRng.reseed(seed)
    // ?zone=N — test seam: open the run at a deeper zone (must be registered).
    const zoneOverride = queryNumber('zone')
    const startZone = (zoneOverride && zoneOverride >= 1 && zoneOverride <= 6 && getZone(zoneOverride) ? zoneOverride : 1) as ZoneId
    const zone = getZone(startZone)
    if (!zone) {
      this.recordError(new Error('no zone registered'))
      return
    }
    const run = new Run(this, this.renderer, runRng, zone.config)
    if (!run.start(startZone)) {
      this.recordError(new Error(`zone ${startZone} failed to start`))
      return
    }
    this.run = run
    this.unpause()
    this.setScreen('run')
    Input.clearMetaKeys()
  }

  finishRun(summary: RunSummary): void {
    this.lastSummary = summary
    this.unpause()
    this.setScreen('summary')
    this.onSummary?.(summary)
    Input.clearMetaKeys()
  }

  toTitle(): void {
    closeOverlay()
    this.unpause()
    if (this.run && !this.run.finished) this.run.endRun('abandon')
    this.run = null
    this.setScreen('title')
    audio.stopMusic()
    Input.clearMetaKeys()
  }

  /** The Threshold (DESIGN §9). Abandons a live run the way toTitle() does. */
  toHub(): void {
    closeOverlay()
    this.unpause()
    if (this.run && !this.run.finished) this.run.endRun('abandon')
    this.run = null
    this.setScreen('hub')
    audio.stopMusic()
    Input.clearMetaKeys()
  }

  // ----------------------------------------------------------------- pause

  pause(): void {
    if (this.screen !== 'run' || !this.run || this.paused) return
    this.paused = true
    audio.sfx('ui')
    showPause(
      this.run,
      () => this.resume(),
      () => this.abandonRun(),
    )
  }

  resume(): void {
    this.unpause()
    Input.clearMetaKeys()
  }

  private unpause(): void {
    this.paused = false
    if (pauseVisible()) hidePause()
  }

  /**
   * The pause menu's "abandon": end the run through the normal path, so the
   * embers bank and the itemised summary shows what the descent was worth.
   */
  abandonRun(): void {
    this.unpause()
    if (this.run && !this.run.finished) this.run.endRun('abandon')
    this.run = null
  }

  seedNext(seed: number): void {
    this.nextSeed = seed >>> 0
  }

  // ---------------------------------------------------------------- update

  step(dt: number): void {
    Input.sample()
    switch (this.screen) {
      case 'title':
        this.titleT += dt
        barks.update(dt)
        fx.update(dt)
        updateParticles(dt)
        // The title is a doorway, not a launcher: it opens onto the Threshold,
        // where the Warden spends embers before descending (DESIGN §9).
        if (Input.anyKeyPressed()) {
          audio.unlock()
          this.toHub()
        }
        break
      case 'run': {
        // Esc: the pause menu. While an altar/relic overlay is up, Esc belongs
        // to the overlay, never to pause — but the edge is drained every frame
        // regardless, so a press ignored under a mandatory overlay cannot fire
        // a stale pause the moment the overlay closes.
        const escEdge = Input.metaKeyPressed('Escape')
        if (this.run && !overlayOpen() && escEdge) {
          if (this.paused) this.resume()
          else this.pause()
        }
        if (this.paused) break
        // The altar and the relic pick are modal: the sim holds still behind
        // them so nothing kills you while you are reading a card.
        if (this.run && !overlayOpen()) this.run.update(dt)
        break
      }
      case 'summary':
        barks.update(dt)
        fx.update(dt)
        updateParticles(dt)
        if (Input.metaKeyPressed('KeyR')) {
          audio.unlock()
          this.startRun()
        }
        break
      case 'hub':
        // The Threshold is a DOM screen (ui/hub.ts) over a dim canvas; there is
        // no simulation to step. Ambient canvas motion only.
        barks.update(dt)
        fx.update(dt)
        updateParticles(dt)
        break
    }
  }

  render(): void {
    const r = this.renderer
    const now = performance.now()
    let realDt = this.lastRenderMs === 0 ? 1 / 60 : (now - this.lastRenderMs) / 1000
    this.lastRenderMs = now
    if (!Number.isFinite(realDt) || realDt <= 0) realDt = 1 / 60
    if (realDt > 0.1) realDt = 0.1
    audio.tick(realDt)
    const pal = this.run?.zone.palette
    r.beginFrame(pal?.bg ?? '#0b0c12')
    const ctx = r.ctx

    if (this.screen === 'run' && this.run) {
      const run = this.run
      // Boss arenas frame the fight, not just the Warden — the boss pulls the
      // camera and the view eases wider so telegraphs are never dodged blind
      // (orchestrator feel pass).
      let camX = run.player.pos.x
      let camY = run.player.pos.y
      // run.boss is non-null through the fight AND the victory celebration
      // (bossVictory removes it at the payout), so the camera holds the corpse
      // while the gold bursts play instead of snapping home on the killing blow.
      const bossCam = run.boss
      const inBossFight = !!(bossCam && run.isBossRoom)
      if (inBossFight && bossCam) {
        camX += (bossCam.pos.x - camX) * 0.42
        camY += (bossCam.pos.y - camY) * 0.42
      }
      const zoomTarget = inBossFight ? 1.08 : WORLD_ZOOM
      r.zoom += (zoomTarget - r.zoom) * Math.min(1, realDt * 3.5)
      r.followCamera(camX, camY, realDt, 7)
      run.render(r.draw)
      this.renderTorchlight(ctx, run)
      renderHud(ctx, run, LOGICAL_W, LOGICAL_H)
      barks.render(ctx, LOGICAL_W, LOGICAL_H, inBossFight ? 'top' : 'bottom')
      fx.renderScreen(ctx, LOGICAL_W, LOGICAL_H)
    } else if (this.screen === 'title') {
      this.renderTitle(ctx)
    } else if (this.screen === 'hub') {
      this.renderHubBackdrop(ctx)
    } else {
      // summary and hub are DOM screens; keep the canvas dark behind them
      ctx.fillStyle = '#07080c'
      ctx.fillRect(0, 0, LOGICAL_W, LOGICAL_H)
    }

    r.endFrame()
  }

  /** Warm pool of light around the Warden; cool stone everywhere else. */
  private renderTorchlight(ctx: CanvasRenderingContext2D, run: Run): void {
    // Baked wall torches breathe: a warm pool per sconce, flicker phased by
    // position so the room shimmers instead of pulsing in lockstep.
    const torches = run.world.torches
    if (torches.length > 0) {
      const t = this.lastRenderMs / 1000
      ctx.globalCompositeOperation = 'lighter'
      for (let i = 0; i < torches.length; i++) {
        const w = torches[i]
        const s = run.renderer.worldToScreen(w.x, w.y, scratch)
        if (s.x < -160 || s.x > LOGICAL_W + 160 || s.y < -160 || s.y > LOGICAL_H + 160) continue
        const fl = 0.82 + 0.18 * Math.sin(t * 7.3 + w.x * 0.37 + w.y * 0.53)
        const rad = 96 * fl
        const g = ctx.createRadialGradient(s.x, s.y, 4, s.x, s.y, rad)
        g.addColorStop(0, 'rgba(255,178,71,0.16)')
        g.addColorStop(1, 'rgba(255,140,40,0)')
        ctx.fillStyle = g
        ctx.fillRect(s.x - rad, s.y - rad, rad * 2, rad * 2)
      }
      ctx.globalCompositeOperation = 'source-over'
    }
    const p = run.renderer.worldToScreen(run.player.pos.x, run.player.pos.y, scratch)
    const g = ctx.createRadialGradient(p.x, p.y, 120, p.x, p.y, 760)
    g.addColorStop(0, 'rgba(255,170,80,0.06)')
    g.addColorStop(0.5, 'rgba(0,0,0,0)')
    g.addColorStop(1, 'rgba(4,4,10,0.42)')
    ctx.fillStyle = g
    ctx.fillRect(0, 0, LOGICAL_W, LOGICAL_H)
  }

  /**
   * Behind the Threshold: a stone landing lit from below by the descent gate.
   * Deliberately quiet — the hub's information is DOM, this is only atmosphere.
   */
  private renderHubBackdrop(ctx: CanvasRenderingContext2D): void {
    this.titleT += 1 / 60
    ctx.fillStyle = '#07080c'
    ctx.fillRect(0, 0, LOGICAL_W, LOGICAL_H)
    const g = ctx.createRadialGradient(
      LOGICAL_W / 2,
      LOGICAL_H + 60,
      40,
      LOGICAL_W / 2,
      LOGICAL_H + 60,
      LOGICAL_H,
    )
    g.addColorStop(0, 'rgba(255,140,60,0.22)')
    g.addColorStop(0.6, 'rgba(120,60,20,0.05)')
    g.addColorStop(1, 'rgba(0,0,0,0)')
    ctx.fillStyle = g
    ctx.fillRect(0, 0, LOGICAL_W, LOGICAL_H)
    ctx.globalAlpha = 0.35
    for (let i = 0; i < 44; i++) {
      const t = this.titleT * 0.28 + i * 2.3
      const x = ((i * 211) % LOGICAL_W) + Math.sin(t) * 14
      const y = LOGICAL_H - ((t * 20 + i * 71) % (LOGICAL_H + 80))
      ctx.fillStyle = i % 3 === 0 ? '#ffb347' : '#8a5a20'
      ctx.fillRect(x, y, 2, 2)
    }
    ctx.globalAlpha = 1
  }

  private renderTitle(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = '#07080c'
    ctx.fillRect(0, 0, LOGICAL_W, LOGICAL_H)
    const cx = LOGICAL_W / 2

    // ember motes
    ctx.globalAlpha = 0.5
    for (let i = 0; i < 60; i++) {
      const t = this.titleT * 0.35 + i * 1.7
      const x = ((i * 137) % LOGICAL_W) + Math.sin(t) * 18
      const y = LOGICAL_H - ((t * 26 + i * 53) % (LOGICAL_H + 80))
      ctx.fillStyle = i % 3 === 0 ? '#ffb347' : '#c9a227'
      ctx.fillRect(x, y, 2, 2)
    }
    ctx.globalAlpha = 1

    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.font = '800 92px ui-monospace, "SF Mono", Menlo, monospace'
    ctx.fillStyle = '#1a1208'
    ctx.fillText('GAUNTLITE', cx + 4, 248)
    const g = ctx.createLinearGradient(0, 200, 0, 300)
    g.addColorStop(0, '#ffd98a')
    g.addColorStop(1, '#c9601f')
    ctx.fillStyle = g
    ctx.fillText('GAUNTLITE', cx, 244)

    ctx.font = '600 20px ui-monospace, "SF Mono", Menlo, monospace'
    ctx.fillStyle = '#9aa0b4'
    ctx.fillText('Descend. Fall. Rise. Deeper.', cx, 306)

    ctx.font = '500 15px ui-monospace, "SF Mono", Menlo, monospace'
    ctx.fillStyle = '#6f7488'
    ctx.fillText('WASD move  ·  MOUSE aim  ·  LMB strike  ·  RMB special', cx, 430)
    ctx.fillText('SPACE dash  ·  Q potion  ·  E interact', cx, 456)

    const pulse = 0.55 + 0.45 * Math.sin(this.titleT * 3)
    ctx.globalAlpha = pulse
    ctx.font = '700 22px ui-monospace, "SF Mono", Menlo, monospace'
    ctx.fillStyle = '#e8dcbf'
    ctx.fillText('PRESS ANY KEY TO DESCEND', cx, 540)
    ctx.globalAlpha = 1

    ctx.font = '500 13px ui-monospace, "SF Mono", Menlo, monospace'
    ctx.fillStyle = '#565b6d'
    ctx.fillText(
      `embers banked: ${meta.data.embers}   ·   runs: ${meta.data.stats.runs}   ·   deepest: zone ${Math.max(1, meta.data.stats.bestZone)}`,
      cx,
      620,
    )
  }

  // -------------------------------------------------------------- snapshot

  snapshot(): Snapshot {
    const run = this.run
    const p = run?.player
    return {
      screen: this.screen,
      zone: run?.zoneId ?? 0,
      roomIndex: run?.room.index ?? 0,
      roomCleared: run?.room.cleared ?? false,
      isRespite: run?.room.isRespite ?? false,
      isBossRoom: run?.isBossRoom ?? false,
      player: {
        hp: p?.hp ?? 0,
        maxHp: p?.maxHp ?? TUNING.player.maxHp,
        x: p?.pos.x ?? 0,
        y: p?.pos.y ?? 0,
        gold: p?.gold ?? 0,
        potions: p?.potions ?? 0,
        alive: p?.alive ?? false,
      },
      gloom: run?.gloomSys.value ?? 0,
      enemies: run?.enemyPool.live.length ?? 0,
      realEnemies: run ? run.enemyPool.live.reduce((n, e) => n + (e.alive && !e.tags.gloomspawn ? 1 : 0), 0) : 0,
      generators: run ? run.generators.reduce((n, g) => n + (g.alive ? 1 : 0), 0) : 0,
      boss: run?.boss
        ? { hp: run.boss.hp, maxHp: run.boss.maxHp, phase: run.boss.phase }
        : (run?.isBossRoom ? run.bossEpitaph : null) ?? null,
      meta: {
        embers: meta.data.embers,
        crests: meta.data.crests,
        zonesReached: meta.data.zonesReached,
      },
      fps: Math.round(this.loop?.fps ?? 0),
      errors: this.errors,
      nav: run ? this.snapshotNav(run) : undefined,
    }
  }

  /** World-space waypoints for the test/balance bots (types.ts Snapshot.nav). */
  private snapshotNav(run: Run): NonNullable<Snapshot['nav']> {
    const T = TILE
    const toWorld = (t: { tx: number; ty: number }): Vec => ({ x: t.tx * T + T / 2, y: t.ty * T + T / 2 })
    const px = run.player.pos.x
    const py = run.player.pos.y
    // Real enemies first (all of them, nearest-first), gloomspawn chaff fills
    // the remaining slots — a hunting bot must never lose the key-carrier in
    // the swarm.
    const near = run.enemyPool.live
      .filter((e) => e.alive)
      .sort((a, b) => {
        const ra = a.tags.gloomspawn ? 1 : 0
        const rb = b.tags.gloomspawn ? 1 : 0
        if (ra !== rb) return ra - rb
        const da = (a.pos.x - px) ** 2 + (a.pos.y - py) ** 2
        const db = (b.pos.x - px) ** 2 + (b.pos.y - py) ** 2
        return da - db
      })
      .slice(0, 8)
      .map((e) => ({ x: e.pos.x, y: e.pos.y, real: !e.tags.gloomspawn }))
    return {
      doors: run.doors.map(toWorld),
      generators: run.generators.filter((g) => g.alive).map((g) => ({ x: g.pos.x, y: g.pos.y })),
      bossDoor: run.room.bossDoor ? toWorld(run.room.bossDoor) : null,
      altar: run.room.altar ? toWorld(run.room.altar) : null,
      fountain: run.room.fountain ? toWorld(run.room.fountain) : null,
      vaultDoors: run.room.vaultDoors.map(toWorld),
      boss: run.boss && run.boss.alive ? { x: run.boss.pos.x, y: run.boss.pos.y } : null,
      enemiesNear: near,
      food: run.pickupPool.live.filter((k) => k.alive && k.kind === 'food').map((k) => ({ x: k.pos.x, y: k.pos.y })),
      keys: run.pickupPool.live.filter((k) => k.alive && k.kind === 'key').map((k) => ({ x: k.pos.x, y: k.pos.y })),
    }
  }
}

export const game = new Game()
